/**
 * Cookies Policy Page
 * Complete cookie policy for Rely platform
 */

import React from 'react';
import { MdCookie } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { Button, HStack } from '@chakra-ui/react';
import LegalPageLayout from '../../components/legal/LegalPageLayout';
import {
  LegalSection,
  LegalParagraph,
  LegalList,
  LegalDisclaimer,
  LegalTable,
  LegalSubSection,
  LegalContact,
  LegalDivider,
} from '../../components/legal/LegalSection';
import { LEGAL_CONFIG } from '../../config/legal';
import { useCookieConsent } from '../../hooks/useCookieConsent';

const CookiesPage = () => {
  const { t } = useTranslation();
  const {
    consentLevel,
    acceptAll,
    acceptEssential,
    resetConsent,
    CONSENT_LEVELS,
  } = useCookieConsent();

  return (
    <LegalPageLayout
      title={t('cookies.title', 'Política de Cookies')}
      subtitle={t(
        'cookies.subtitle',
        'Información sobre cómo utilizamos cookies y tecnologías similares'
      )}
      documentType="cookies"
      icon={MdCookie}
    >
      {/* Cookie Preferences Box */}
      <LegalDisclaimer
        type="info"
        title={t('cookies.preferences.title', 'Sus Preferencias de Cookies')}
      >
        <LegalParagraph>
          {consentLevel === CONSENT_LEVELS.ALL
            ? t('cookies.preferences.all', 'Ha aceptado todas las cookies.')
            : consentLevel === CONSENT_LEVELS.ESSENTIAL
            ? t(
                'cookies.preferences.essential',
                'Ha aceptado solo cookies esenciales.'
              )
            : t(
                'cookies.preferences.none',
                'No ha configurado sus preferencias de cookies.'
              )}
        </LegalParagraph>
        <HStack spacing={3} mt={4}>
          <Button size="sm" colorScheme="brand" onClick={acceptAll}>
            {t('cookies.preferences.acceptAll', 'Aceptar Todas')}
          </Button>
          <Button size="sm" variant="outline" onClick={acceptEssential}>
            {t('cookies.preferences.essentialOnly', 'Solo Esenciales')}
          </Button>
          <Button size="sm" variant="ghost" onClick={resetConsent}>
            {t('cookies.preferences.reset', 'Restablecer')}
          </Button>
        </HStack>
      </LegalDisclaimer>

      {/* Introduction */}
      <LegalParagraph>
        {t(
          'cookies.intro',
          `Esta Política de Cookies explica qué son las cookies, cómo ${LEGAL_CONFIG.company.name} las utiliza, y cómo puede gestionarlas.`
        )}
      </LegalParagraph>

      {/* Section 1: What Are Cookies */}
      <LegalSection
        number={1}
        title={t('cookies.what.title', 'Que son las Cookies')}
        id="what-are-cookies"
      >
        <LegalParagraph>
          {t(
            'cookies.what.description',
            'Las cookies son pequeños archivos de texto que se almacenan en su dispositivo (computadora, tablet o teléfono) cuando visita un sitio web. Se utilizan ampliamente para hacer que los sitios web funcionen de manera más eficiente y para proporcionar información a los propietarios del sitio.'
          )}
        </LegalParagraph>
        <LegalParagraph>
          {t('cookies.what.types', 'Existen diferentes tipos de cookies:')}
        </LegalParagraph>
        <LegalList
          items={[
            t(
              'cookies.what.session',
              'Cookies de Sesion: Se eliminan cuando cierra su navegador'
            ),
            t(
              'cookies.what.persistent',
              'Cookies Persistentes: Permanecen en su dispositivo por un periodo especifico'
            ),
            t(
              'cookies.what.firstParty',
              'Cookies Propias: Establecidas por el sitio que visita'
            ),
            t(
              'cookies.what.thirdParty',
              'Cookies de Terceros: Establecidas por otros servicios'
            ),
          ]}
        />
      </LegalSection>

      {/* Section 2: Essential Cookies */}
      <LegalSection
        number={2}
        title={t('cookies.essential.title', 'Cookies Esenciales')}
        id="essential-cookies"
      >
        <LegalParagraph>
          {t(
            'cookies.essential.description',
            'Estas cookies son necesarias para que el sitio funcione correctamente. No pueden ser desactivadas.'
          )}
        </LegalParagraph>
        <LegalTable
          headers={[
            t('cookies.table.name', 'Nombre'),
            t('cookies.table.purpose', 'Proposito'),
            t('cookies.table.duration', 'Duracion'),
            t('cookies.table.provider', 'Proveedor'),
          ]}
          rows={LEGAL_CONFIG.cookies.essential.map((cookie) => [
            cookie.name,
            cookie.purpose,
            cookie.duration,
            cookie.provider,
          ])}
        />
      </LegalSection>

      {/* Section 3: Functional Cookies */}
      <LegalSection
        number={3}
        title={t('cookies.functional.title', 'Cookies Funcionales')}
        id="functional-cookies"
      >
        <LegalParagraph>
          {t(
            'cookies.functional.description',
            'Estas cookies permiten recordar sus preferencias para mejorar su experiencia.'
          )}
        </LegalParagraph>
        <LegalTable
          headers={[
            t('cookies.table.name', 'Nombre'),
            t('cookies.table.purpose', 'Proposito'),
            t('cookies.table.duration', 'Duracion'),
            t('cookies.table.provider', 'Proveedor'),
          ]}
          rows={LEGAL_CONFIG.cookies.functional.map((cookie) => [
            cookie.name,
            cookie.purpose,
            cookie.duration,
            cookie.provider,
          ])}
        />
      </LegalSection>

      {/* Section 4: Third-Party Cookies */}
      <LegalSection
        number={4}
        title={t('cookies.thirdParty.title', 'Cookies de Terceros')}
        id="third-party-cookies"
      >
        <LegalParagraph>
          {t(
            'cookies.thirdParty.description',
            'Utilizamos servicios de terceros que pueden establecer sus propias cookies:'
          )}
        </LegalParagraph>

        <LegalSubSection title="Auth0">
          <LegalParagraph>
            {t(
              'cookies.thirdParty.auth0',
              'Utilizamos Auth0 para la autenticacion segura. Auth0 puede establecer cookies para mantener su sesion.'
            )}
          </LegalParagraph>
        </LegalSubSection>

        <LegalSubSection title="Stripe">
          <LegalParagraph>
            {t(
              'cookies.thirdParty.stripe',
              'Stripe procesa nuestros pagos y puede establecer cookies para prevencion de fraude y procesamiento de transacciones.'
            )}
          </LegalParagraph>
        </LegalSubSection>

        <LegalSubSection title="Cloudflare">
          <LegalParagraph>
            {t(
              'cookies.thirdParty.cloudflare',
              'Utilizamos Cloudflare para seguridad y rendimiento. Cloudflare puede establecer cookies para identificar trafico malicioso.'
            )}
          </LegalParagraph>
        </LegalSubSection>
      </LegalSection>

      {/* Section 5: Managing Cookies */}
      <LegalSection
        number={5}
        title={t('cookies.manage.title', 'Como Gestionar las Cookies')}
        id="manage-cookies"
      >
        <LegalParagraph>
          {t(
            'cookies.manage.intro',
            'Puede gestionar las cookies de varias maneras:'
          )}
        </LegalParagraph>

        <LegalSubSection
          title={t('cookies.manage.banner.title', 'Banner de Cookies')}
        >
          <LegalParagraph>
            {t(
              'cookies.manage.banner.description',
              'Puede aceptar o rechazar cookies no esenciales utilizando el banner de cookies que aparece en su primera visita, o utilizando los botones al principio de esta página.'
            )}
          </LegalParagraph>
        </LegalSubSection>

        <LegalSubSection
          title={t(
            'cookies.manage.browser.title',
            'Configuración del Navegador'
          )}
        >
          <LegalParagraph>
            {t(
              'cookies.manage.browser.description',
              'La mayoría de los navegadores le permiten controlar las cookies a través de su configuración. Puede:'
            )}
          </LegalParagraph>
          <LegalList
            items={[
              t('cookies.manage.browser.view', 'Ver las cookies almacenadas'),
              t('cookies.manage.browser.delete', 'Eliminar cookies existentes'),
              t(
                'cookies.manage.browser.block',
                'Bloquear cookies de ciertos sitios'
              ),
              t(
                'cookies.manage.browser.blockAll',
                'Bloquear todas las cookies'
              ),
            ]}
          />
        </LegalSubSection>

        <LegalDisclaimer
          type="warning"
          title={t('cookies.manage.warning.title', 'Advertencia')}
        >
          {t(
            'cookies.manage.warning.text',
            'Bloquear todas las cookies puede afectar la funcionalidad del sitio. Las cookies esenciales son necesarias para el funcionamiento basico de la plataforma.'
          )}
        </LegalDisclaimer>
      </LegalSection>

      {/* Section 6: Browser Instructions */}
      <LegalSection
        number={6}
        title={t('cookies.browsers.title', 'Instrucciones por Navegador')}
        id="browser-instructions"
      >
        <LegalParagraph>
          {t(
            'cookies.browsers.intro',
            'Aquí encontrará enlaces a las instrucciones de gestión de cookies para los navegadores más populares:'
          )}
        </LegalParagraph>
        <LegalList
          items={[
            'Google Chrome: chrome://settings/cookies',
            'Mozilla Firefox: about:preferences#privacy',
            'Safari: Preferencias > Privacidad',
            'Microsoft Edge: edge://settings/privacy',
          ]}
        />
      </LegalSection>

      {/* Section 7: Updates */}
      <LegalSection
        number={7}
        title={t('cookies.updates.title', 'Actualizaciones de esta Política')}
        id="updates"
      >
        <LegalParagraph>
          {t(
            'cookies.updates.text',
            'Podemos actualizar esta Política de Cookies periódicamente para reflejar cambios en las cookies que utilizamos o por otras razones operativas, legales o regulatorias. Por favor, visite esta página regularmente para estar informado sobre nuestro uso de cookies.'
          )}
        </LegalParagraph>
      </LegalSection>

      <LegalDivider />

      {/* Contact Information */}
      <LegalSection title={t('cookies.contact.title', 'Contacto')} id="contact">
        <LegalParagraph>
          {t(
            'cookies.contact.text',
            'Si tiene preguntas sobre nuestra Política de Cookies:'
          )}
        </LegalParagraph>
        <LegalContact
          label={t('cookies.contact.privacy', 'Privacidad')}
          email={LEGAL_CONFIG.contact.privacy}
          description={t(
            'cookies.contact.description',
            'Para preguntas sobre cookies y privacidad'
          )}
        />
      </LegalSection>
    </LegalPageLayout>
  );
};

export default CookiesPage;
